import { relativeTime } from '@/shared/format'
import { formatMoney } from '@/features/billing/proforma/money'
import type { ProformaStatus } from '@/features/billing/proforma/types'

/**
 * The landing page's small decisions, kept free of React so they can be
 * tested as plain statements: how a wait is worded, which statuses make up a
 * stage of the pipeline.
 */

// Time of day is shared with the login page; re-exported for this page's imports.
export { GREETING, jakartaHour, timeOfDay, type TimeOfDay } from '@/shared/timeOfDay'

/** "IDR 96.200.000", "USD 12.480,00"; a dash when there is no amount yet. */
export const money = formatMoney

/**
 * How long something has waited, as a length rather than a moment:
 * "26 jam", not "26 jam lalu" -- it follows "menunggu", which already says
 * it is ongoing.
 */
export function waitedFor(since: string | null | undefined, now: Date = new Date()): string {
  const phrase = relativeTime(since, now)
  if (phrase === 'baru saja') return 'kurang dari semenit'
  return phrase.endsWith(' lalu') ? phrase.slice(0, -' lalu'.length) : phrase
}

/**
 * The five stages the landing page draws, and which raw statuses fall into
 * each. Here and not on the server, so the grouping can change without a
 * backend release. Returned and failed documents are not a stage: they are
 * loops back, and are shown as such under the pipeline.
 */
export interface PipelineStage {
  label: string
  hint: string
  statuses: ProformaStatus[]
  /** A token colour, so the ring matches the status pills. */
  color: string
}

export const PIPELINE_STAGES: PipelineStage[] = [
  { label: 'Draft', hint: 'belum diajukan', statuses: ['DRAFT'], color: 'var(--sp-gray-500)' },
  { label: 'Menunggu persetujuan', hint: 'di penyetuju', statuses: ['PENDING_APPROVAL'], color: 'var(--sp-warning)' },
  { label: 'Disetujui', hint: 'siap dikirim', statuses: ['APPROVED'], color: 'var(--sp-success)' },
  { label: 'Terkirim ke ERP', hint: 'menunggu invoice', statuses: ['POSTED', 'PARTIALLY_POSTED'], color: 'var(--sp-primary)' },
  { label: 'Sudah ditagihkan', hint: 'invoice terbit', statuses: ['INVOICED', 'CLOSED'], color: 'var(--sp-info)' },
]

export function countOf(byStatus: Partial<Record<ProformaStatus, number>>, statuses: ProformaStatus[]): number {
  return statuses.reduce((sum, status) => sum + (byStatus[status] ?? 0), 0)
}

/**
 * "Masih berjalan" on the recent list: anything someone still has to act on.
 * The rest -- posted, invoiced, closed, rejected, cancelled -- is "Selesai".
 */
export const IN_PROGRESS: ReadonlySet<ProformaStatus> = new Set<ProformaStatus>([
  'DRAFT',
  'PENDING_APPROVAL',
  'RETURNED',
  'APPROVED',
  'POST_FAILED',
  'PARTIALLY_POSTED',
])

/** "September 2026", from the pipeline's yyyy-MM-dd start date. */
export function monthLabel(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number)
  if (!year || !month) return ''
  return new Intl.DateTimeFormat('id-ID', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  )
}
